import { useEffect, useState } from "react";
import moment from "moment";
import { useFormik } from "formik";
import * as yup from "yup";
import { Dialog, DialogBody, Button, Tooltip } from "@material-tailwind/react";
import Select from "react-select";
import { toast } from "react-toast";

/* COMPONENTS */
import Input from "components/input";

/* PACKAGES */
import { Helmet } from "react-helmet";

/* API */
import {
  deleteUserAPI,
  getUsersList,
  updateNewUserAPI,
} from "service/api/auth.api";
import { createNewUserAPI } from "service/api/auth.api";

/* REACT ICONS */
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { AiFillEdit } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";

const zones = [
  { value: "OFFICE-BEARER", label: "Office bearer" },
  { value: "JURY", label: "Jury" },
];

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email().required("Email is required"),
  password: yup.string().required("Password is required"),
  role: yup.object().required("Role is required"),
});

const updateValidationSchema = yup.object().shape({
  password: yup.string(),
});

const WorkforceAdmin = () => {
  /* LOADING STATE */
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  /* STATE VARIABLES */
  const [togglePassword, setTogglePassword] = useState(false);

  /* CREATE USER DIALOG */
  const [createUserDialog, setCreateUserDialog] = useState(false);

  /* DELETE USER DIALOG */
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  /* UPDATE USER DIALOG */
  const [updateUserDialog, setUpdateUserDialog] = useState();
  const [originalUserData, setOriginalUserData] = useState(null);

  /* USERS LIST */
  const [usersList, setUsersList] = useState([]);

  /* GET LIST OF USERS */
  const getAllUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsersList(1, 50, true);
      setUsersList(response?.data?.docs);
    } catch (e) {
      console.log("Error: ", e);
    } finally {
      setLoading(false);
    }
  };

  /* FORM HANDLER */
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        selectedRole: values?.role?.value,
      };

      createNewUserAPI(payload)
        .then((res) => {
          setFormLoading(true);
          if (res?.status) {
            toast.success(res?.message);
            setCreateUserDialog(!createUserDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          setLoading(false);
          if (e?.response?.data?.message) {
            toast.error(e?.response?.data?.message);
          } else {
            toast.error(e?.message);
          }
        })
        .finally((res) => {
          setFormLoading(false);
          getAllUsers();
        });
    },
  });

  /* HELPER FUNCTION TO BUILD UPDATE PAYLOAD */
  const buildUpdatePayload = (currentValues, originalData) => {
    const payload = {};

    // Check each field for changes
    if (currentValues.firstName !== originalData.firstName) {
      payload.firstName = currentValues.firstName;
    }

    if (currentValues.lastName !== originalData.lastName) {
      payload.lastName = currentValues.lastName;
    }

    if (currentValues.email !== originalData.email) {
      payload.email = currentValues.email;
    }

    // Only include password if it's not empty (user wants to change it)
    if (currentValues.password && currentValues.password.trim() !== "") {
      payload.password = currentValues.password;
    }

    // Check if role has changed (compare values, not objects)
    const currentRoleValue = currentValues.role?.value;
    const originalRoleValue =
      originalData.role?.value || originalData.designation;

    if (currentRoleValue && currentRoleValue !== originalRoleValue) {
      payload.selectedRole = currentRoleValue;
    }

    return payload;
  };

  /* UPDATE FORM HANDLER */
  const updateFormik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
    },
    validationSchema: updateValidationSchema,
    onSubmit: (values) => {
      const payload = buildUpdatePayload(values, originalUserData);
      if (Object.keys(payload).length === 0) {
        toast.info("No changes detected");
        return;
      }

      updateNewUserAPI(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message);
            setUpdateUserDialog(false);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          if (e?.response?.data?.message) {
            toast.error(e?.response?.data?.message);
          } else {
            toast.error(e?.message);
          }
        })
        .finally((res) => {
          getAllUsers();
        });
    },
  });

  /* DELETE USER */
  const deleteUserHandler = async () => {
    try {
      const response = await deleteUserAPI(selectedId);
      if (response?.status) {
        toast.success(response?.message);
        getAllUsers();
      } else {
        toast.error(response?.message);
      }
    } catch (e) {
      console.log("Error: ", e);
    } finally {
      setDeleteUserDialog(false);
    }
  };

  /* HANDLE EDIT USER CLICK */
  const handleEditUser = (user) => {
    setOriginalUserData({
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      role: { value: user?.designation, label: user?.designation },
    });

    updateFormik.setFieldValue("firstName", user?.firstName);
    updateFormik.setFieldValue("lastName", user?.lastName);
    updateFormik.setFieldValue("email", user?.email);
    updateFormik.setFieldValue("password", "");
    updateFormik.setFieldValue("role", {
      value: user?.designation,
      label:
        zones.find((zone) => zone.value === user?.designation)?.label ||
        user?.designation,
    });

    setSelectedId(user?._id);
    setUpdateUserDialog(true);
  };

  /* USE EFFECT */
  useEffect(() => {
    getAllUsers();
  }, []);

  return (
    <section className="min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Student Applications | Admin Panel</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="flex items-center justify-between">
          <div className="p-0">
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Workforce Administration
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage and view the teammates within your organizations
            </h2>
          </div>
          <button
            onClick={() => setCreateUserDialog(!createUserDialog)}
            className="bg-logoRed hover:bg-logoRed/80 transition-all duration-300 ease-in-out rounded-md text-sm font-ibm-regular text-white px-5 py-2.5 normal-case"
          >
            Add new user
          </button>
        </div>

        {/* BODY */}
        <div className="mt-5">
          {loading ? (
            <div className="w-full bg-white border border-greyBorder rounded-lg py-5">
              <p className="text-base font-ibm-semibold text-black text-center">
                Please wait while we are loading the users list...
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-5">
              {usersList?.map((user, index) => {
                console.log("User: ", user);
                return (
                  <div key={index} className="col-span-1">
                    <div className="bg-white border border-greyBorder rounded-lg px-5 pb-2 relative">
                      <div className="py-5 border-b border-greyBorder">
                        <div className="p-0">
                          {user?.designation === "OFFICE-BEARER" ? (
                            <div className="w-fit px-2 py-1 text-xs font-ibm-regular text-green-600 bg-green-500/10 rounded-md">
                              {user.designation}
                            </div>
                          ) : (
                            <div className="w-fit px-2 py-1 text-xs font-ibm-regular text-orange-600 bg-orange-500/10 rounded-md">
                              {user.designation}
                            </div>
                          )}
                        </div>
                        <div className="mt-3">
                          <h5 className="text-base font-ibm-semibold text-black">
                            {user?.firstName} {user?.lastName}
                          </h5>
                          <p className="text-base font-ibm-regular text-greyText">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                      <div className="py-3">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-ibm-regular text-greyText">
                            Created on
                          </h5>
                          <p className="text-sm font-ibm-regular text-black">
                            {moment(user?.createdAt).format("DD-MM-YYYY")}
                          </p>
                        </div>
                      </div>

                      <div className="absolute top-3 right-3">
                        <div className="flex items-center gap-2">
                          <Tooltip
                            content="Edit"
                            className="text-sm font-ibm-regular text-white"
                          >
                            <Button
                              onClick={() => handleEditUser(user)}
                              className="shadow-none hover:shadow-none p-2 bg-greyBg/50 rounded-full border border-greyBorder hover:border-black/30 transition-all duration-300 ease-in-out"
                            >
                              <AiFillEdit className="w-4 h-4 text-greyText" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            content="Remove"
                            className="text-sm font-ibm-regular text-white"
                          >
                            <Button
                              onClick={() => {
                                setDeleteUserDialog(!deleteUserDialog);
                                setSelectedId(user?._id);
                              }}
                              className="shadow-none hover:shadow-none p-2 bg-greyBg/50 rounded-full border border-greyBorder hover:border-black/30 transition-all duration-300 ease-in-out"
                            >
                              <FaTrash className="w-3 h-3 text-greyText" />
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* CREATE USER DIALOG */}
      <Dialog
        open={createUserDialog}
        handler={() => setCreateUserDialog(!createUserDialog)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Create user
              </h5>
              <Button
                onClick={() => setCreateUserDialog(!createUserDialog)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <form onSubmit={formik.handleSubmit} className="p-5">
            {/* NAME */}
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-1">
                <label className="text-sm font-ibm-regular text-greyText">
                  First name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="firstName"
                    id="firstName"
                    placeholder="First Name"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    error={
                      formik.errors.firstName && formik.touched.firstName
                        ? true
                        : false
                    }
                  />
                </div>
              </div>
              <div className="col-span-1">
                <label className="text-sm font-ibm-regular text-greyText">
                  Last name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="lastName"
                    id="lastName"
                    placeholder="Last Name"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    error={
                      formik.errors.lastName && formik.touched.lastName
                        ? true
                        : false
                    }
                  />
                </div>
              </div>
            </div>

            {/* EMAIL ADDRESS */}
            <div className="form-group mt-5">
              <label className="text-sm font-ibm-regular text-greyText">
                Email address <span className="text-red-500">*</span>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="email"
                    id="email"
                    placeholder="Email Address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={
                      formik.errors.email && formik.touched.email ? true : false
                    }
                  />
                </div>
              </label>
            </div>

            {/* PASSWORD */}
            <div className="form-group mt-5">
              <label className="text-sm font-ibm-regular text-greyText">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <Input
                  type={togglePassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={
                    formik.errors.password && formik.touched.password
                      ? true
                      : false
                  }
                />
                <Button
                  className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 !absolute top-2 right-2"
                  onClick={() => setTogglePassword(!togglePassword)}
                >
                  {togglePassword ? (
                    <IoMdEyeOff className="w-5 h-5 text-greyText" />
                  ) : (
                    <IoMdEye className="w-5 h-5 text-greyText" />
                  )}
                </Button>
              </div>
            </div>

            {/* ROLE */}
            <div className="mt-5">
              <label className="text-sm font-ibm-regular text-greyText">
                Role <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <Select
                  options={zones}
                  className="react-select"
                  classNamePrefix={
                    formik.errors.role && formik.touched.role
                      ? "dropdown_error"
                      : "dropdown"
                  }
                  value={formik.values.role}
                  onChange={(e) => formik.setFieldValue("role", e)}
                  placeholder="Select role"
                />
              </div>
            </div>

            {/* ACTIONS */}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2.5 px-5 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white normal-case"
              >
                {formLoading ? "Please wait..." : "Create user"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* UPDATE USER DIALOG */}
      <Dialog
        open={updateUserDialog}
        handler={() => setUpdateUserDialog(!updateUserDialog)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Edit user
              </h5>
              <Button
                onClick={() => setUpdateUserDialog(!updateUserDialog)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormik.handleSubmit} className="p-5">
            {/* NAME */}
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-1">
                <label className="text-sm font-ibm-regular text-greyText">
                  First name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="firstName"
                    id="firstName"
                    placeholder="First Name"
                    value={updateFormik.values.firstName}
                    onChange={updateFormik.handleChange}
                    error={
                      updateFormik.errors.firstName &&
                      updateFormik.touched.firstName
                        ? true
                        : false
                    }
                  />
                </div>
              </div>
              <div className="col-span-1">
                <label className="text-sm font-ibm-regular text-greyText">
                  Last name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="lastName"
                    id="lastName"
                    placeholder="Last Name"
                    value={updateFormik.values.lastName}
                    onChange={updateFormik.handleChange}
                    error={
                      updateFormik.errors.lastName &&
                      updateFormik.touched.lastName
                        ? true
                        : false
                    }
                  />
                </div>
              </div>
            </div>

            {/* EMAIL ADDRESS */}
            <div className="form-group mt-5">
              <label className="text-sm font-ibm-regular text-greyText">
                Email address <span className="text-red-500">*</span>
                <div className="mt-1">
                  <Input
                    type="text"
                    name="email"
                    id="email"
                    placeholder="Email Address"
                    value={updateFormik.values.email}
                    onChange={updateFormik.handleChange}
                    error={
                      updateFormik.errors.email && updateFormik.touched.email
                        ? true
                        : false
                    }
                  />
                </div>
              </label>
            </div>

            {/* PASSWORD */}
            <div className="form-group mt-5">
              <label className="text-sm font-ibm-regular text-greyText">
                New Password (leave empty to keep current password)
              </label>
              <div className="mt-1 relative">
                <Input
                  type={togglePassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="New Password (optional)"
                  value={updateFormik.values.password}
                  onChange={updateFormik.handleChange}
                  error={
                    updateFormik.errors.password &&
                    updateFormik.touched.password
                      ? true
                      : false
                  }
                />
                <Button
                  className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 !absolute top-2 right-2"
                  onClick={() => setTogglePassword(!togglePassword)}
                >
                  {togglePassword ? (
                    <IoMdEyeOff className="w-5 h-5 text-greyText" />
                  ) : (
                    <IoMdEye className="w-5 h-5 text-greyText" />
                  )}
                </Button>
              </div>
            </div>

            {/* ROLE */}
            <div className="mt-5">
              <label className="text-sm font-ibm-regular text-greyText">
                Role <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <Select
                  options={zones}
                  className="react-select"
                  classNamePrefix={
                    updateFormik.errors.role && updateFormik.touched.role
                      ? "dropdown_error"
                      : "dropdown"
                  }
                  value={updateFormik.values.role}
                  onChange={(e) => updateFormik.setFieldValue("role", e)}
                  placeholder="Select role"
                />
              </div>
            </div>

            {/* ACTIONS */}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2.5 px-5 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white normal-case"
              >
                Update user
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* DELETE USER DIALOG */}
      <Dialog
        open={deleteUserDialog}
        handler={() => setDeleteUserDialog(false)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-2 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Confirmation
            </h5>
            <Button className="shadow-none hover:shadow-none p-2 rounded-full bg-white hover:bg-black/10 transition-all duration-300 ease-in-out">
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>

          <div className="p-5">
            <p className="text-sm font-ibm-regular text-greyText">
              Are you sure you want to delete this user?
            </p>
            {/* ACTIONS */}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={deleteUserHandler}
                className="py-2.5 px-5 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white normal-case"
              >
                Yes, delete
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default WorkforceAdmin;
